import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useSocket } from '../context/SocketContext';
import { tradeService } from '../services/tradeService';
import UserLanguageSwitcher from '../components/common/UserLanguageSwitcher';
import { ALL_ASSETS } from '../constants/assets';
import { toast } from 'react-hot-toast';
import { userService } from '../services/userService';
import './MajesticCommon.css';
import './HoldingsPage.css';

const HoldingsPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [holdings, setHoldings] = useState([]);
    const [user, setUser] = useState({ currency: 'None' });
    const [loading, setLoading] = useState(true);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const socket = useSocket();

    // Filter State
    const [filterAsset, setFilterAsset] = useState('ALL');
    const [filterType, setFilterType] = useState('ALL');
    const [filterDuration, setFilterDuration] = useState('ALL');
    const [filterLeverage, setFilterLeverage] = useState('ALL');

    const resetFilters = () => {
        setFilterAsset('ALL');
        setFilterType('ALL');
        setFilterDuration('ALL');
        setFilterLeverage('ALL');
    };

    useEffect(() => {
        fetchProfile();
        fetchHoldings();
        // Removed polling
    }, []);

    useEffect(() => {
        if (!socket) return;

        const handleResult = () => {
            fetchHoldings(); // Refresh list on trade conclusion
        };

        const handleBalanceUpdate = (data) => {
            if (data.balance !== undefined) {
                setUser(prev => ({ ...prev, accountBalance: data.balance }));
            }
        };

        socket.on('trade_result', handleResult);
        socket.on('balance_update', handleBalanceUpdate);

        return () => {
            socket.off('trade_result', handleResult);
            socket.off('balance_update', handleBalanceUpdate);
        };
    }, [socket]);

    const fetchProfile = async () => {
        try {
            const data = await tradeService.getProfile ? await tradeService.getProfile() : await userService.getProfile();
            if (data.success) {
                setUser(data.user);
            }
        } catch (error) {
            console.error('Failed to fetch profile:', error);
        }
    };

    const fetchHoldings = async () => {
        try {
            const data = await tradeService.getHistory();
            if (data.success) {
                setHoldings(data.trades);
            }
        } catch (error) {
            console.error('Failed to fetch holdings:', error);
            toast.error(t('failed_load_holdings', 'Failed to load holdings'));
        } finally {
            setLoading(false);
        }
    };

    const uniqueAssets = useMemo(() => {
        return ['ALL', ...ALL_ASSETS];
    }, []);

    const filteredHoldings = useMemo(() => {
        return holdings.filter(h => {
            const assetMatch = filterAsset === 'ALL' || h.pair === filterAsset;
            const typeMatch = filterType === 'ALL' ||
                (filterType === 'BUY' && h.type === 'buy') ||
                (filterType === 'SELL' && h.type === 'sell');
            const durationMatch = filterDuration === 'ALL' || h.duration === parseInt(filterDuration);
            const leverageMatch = filterLeverage === 'ALL' || h.leverage === parseInt(filterLeverage);
            return assetMatch && typeMatch && durationMatch && leverageMatch;
        });
    }, [holdings, filterAsset, filterType, filterDuration, filterLeverage]);

    const stats = useMemo(() => {
        if (!filteredHoldings.length) return { totalPnL: 0, winRate: 0, activeCount: 0 };
        const totalPnL = filteredHoldings.reduce((acc, h) => acc + (h.pnl || 0), 0);
        const wins = filteredHoldings.filter(h => h.pnl > 0).length; // Positive is profit
        const winRate = ((wins / filteredHoldings.length) * 100).toFixed(1);
        return { totalPnL, winRate, activeCount: filteredHoldings.length };
    }, [filteredHoldings]);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const d = new Date(dateString);
        const pad = (n) => n.toString().padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };

    const currencyMap = {
        'None': '', 'USD': '$', 'GBP': '£', 'BTC': '₿', 'EUR': '€', 'CHF': '₣',
        'KRW': '₩', 'TRY': '₺', 'INR': '₹', 'CNY': '¥', 'THB': '฿',
        'RUB': '₽', 'UAH': '₴', 'BDT': '৳', 'JPY': '¥', 'ILS': '₪',
        'AED': 'د.إ', 'SAR': 'ر.س'
    };
    const currencySymbol = user?.username !== undefined && user?.currency !== undefined ? (currencyMap[user.currency] !== undefined ? currencyMap[user.currency] : '') : '';

    return (
        <div className="majestic-page-wrapper holdings-dashboard-wrapper" style={{ paddingBottom: '90px' }}>
            {/* Majestic Header */}
            <div className="majestic-header d-flex align-items-center px-3 py-3 justify-content-between">
                <div style={{ width: '40px' }}>
                    <i className="bi bi-chevron-left text-white fs-4 cursor-pointer" onClick={() => navigate(-1)}></i>
                </div>
                <div className="flex-grow-1 text-center d-flex flex-column align-items-center justify-content-center">
                    <h1 className="majestic-header-title m-0" style={{ fontSize: '18px' }}>
                        {t('holdings', 'Holdings')}
                    </h1>
                    <div className="text-warning fw-bold" style={{ fontSize: '12px' }}>
                        {currencySymbol}{user.accountBalance ? Number(user.accountBalance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                    </div>
                </div>
                <div style={{ width: '40px' }} className="d-flex justify-content-end">
                    <UserLanguageSwitcher />
                </div>
            </div>

            <div className="majestic-container animate-slide-up">
                {/* Portfolio Summary */}
                <div className="portfolio-summary-container p-0 mb-4">
                    <div className="summary-glass-card shadow-lg" style={{ border: '1px solid rgba(252, 213, 53, 0.3)' }}>
                        <div className="summary-label">{t('total_portfolio_pnl', 'Total Portfolio PnL')}</div>
                        <div className="total-equity" style={{ color: stats.totalPnL >= 0 ? '#0ecb81' : '#f6465d' }}>
                            {stats.totalPnL >= 0 ? '+' : '-'}{currencySymbol}{Math.abs(stats.totalPnL).toFixed(2)}
                        </div>
                        <div className="stats-row">
                            <div className="stat-box">
                                <span className="stat-label">{t('active_trades', 'TOTAL TRADES')}</span>
                                <span className="stat-val text-white">{stats.activeCount}</span>
                            </div>
                            <div className="stat-box">
                                <span className="stat-label">{t('win_rate', 'WIN RATE')}</span>
                                <span className="stat-val text-warning">{stats.winRate}%</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Holdings List */}
                <div className="holdings-list-section p-0">
                    <div className="section-header">
                        <span className="section-title text-uppercase opacity-50 small fw-bold" style={{ letterSpacing: '1px' }}>{t('recent_activity', 'RECENT ACTIVITY')}</span>
                        <div
                            className={`p-2 rounded-3 cursor-pointer ${filterAsset !== 'ALL' || filterType !== 'ALL' || filterDuration !== 'ALL' || filterLeverage !== 'ALL' ? 'bg-warning text-dark' : 'text-white'}`}
                            style={filterAsset === 'ALL' && filterType === 'ALL' && filterDuration === 'ALL' && filterLeverage === 'ALL' ? { background: 'rgba(255, 255, 255, 0.05)' } : {}}
                            onClick={() => setIsFilterOpen(true)}
                        >
                            <i className="bi bi-sliders2-vertical"></i>
                        </div>
                    </div>

                    {loading ? (
                        <div className="d-flex flex-column gap-3 mt-3">
                            {[1, 2, 3].map(i => <div key={i} className="skeleton-card" style={{ height: '140px' }}></div>)}
                        </div>
                    ) : filteredHoldings.length === 0 ? (
                        <div className="empty-holdings-state py-5 mt-4">
                            <div className="p-4 rounded-circle d-inline-block mb-3" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
                                <i className="bi bi-inbox empty-icon m-0 opacity-50"></i>
                            </div>
                            <h5 className="text-white opacity-75">{t('no_holdings_found', 'No holdings found')}</h5>
                            <p className="text-muted small mb-4">{t('try_adjusting_filters', 'Try adjusting your filters.')}</p>
                            <button className="btn majestic-btn-primary px-5" onClick={resetFilters}>
                                {t('clear_filters', 'Clear Filters')}
                            </button>
                        </div>
                    ) : (
                        <div className="d-flex flex-column gap-3 mt-2">
                            {filteredHoldings.map((item) => {
                                const isBuy = item.type === 'buy';
                                const isProfit = (item.pnl || 0) > 0;
                                const pnlClass = isProfit ? 'pnl-profit' : 'pnl-loss';

                                return (
                                    <div key={item.id} className="trade-card-robust shadow-sm">
                                        {/* Card Header */}
                                        <div className="card-header-row d-flex justify-content-between align-items-center mb-3">
                                            <div className="d-flex align-items-center gap-3">
                                                <span className="symbol-badge-pill">{item.pair}</span>
                                                <span className={`direction-text ${isBuy ? 'buy' : 'sell'}`}>
                                                    {isBuy ? t('buy_up', 'BUY UP') : t('buy_down', 'BUY DOWN')}
                                                </span>
                                            </div>
                                            <div className="status-label">{t('resolved', 'Resolved')}</div>
                                        </div>

                                        {/* Price Flow Section */}
                                        <div className="price-flow-robust mb-3">
                                            <div className="row g-0 align-items-end">
                                                <div className="col-auto">
                                                    <div className="label-xs">{t('entry', 'ENTRY')}</div>
                                                    <div className="price-val">{item.entryPrice?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}</div>
                                                </div>
                                                <div className="col-auto px-3 pb-1">
                                                    <i className="bi bi-arrow-right text-secondary opacity-25"></i>
                                                </div>
                                                <div className="col-auto">
                                                    <div className="label-xs">{t('exit', 'EXIT')}</div>
                                                    <div className={`price-val ${pnlClass}`}>{(item.exitPrice || item.entryPrice)?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}</div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="horizontal-separator mb-3"></div>

                                        {/* Investment & Results Grid */}
                                        <div className="details-grid-robust">
                                            <div className="row g-3">
                                                <div className="col-6">
                                                    <div className="label-xs">{t('investment', 'INVESTMENT')}</div>
                                                    <div className="value-md font-weight-bold">{currencySymbol}{item.amount?.toFixed(2)}</div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="label-xs">{t('leverage', 'LEVERAGE')}</div>
                                                    <div className="value-md font-weight-bold">x{item.leverage || 1}</div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="label-xs">{t('duration', 'DURATION')}</div>
                                                    <div className="value-md font-weight-bold">{item.duration}{t('seconds_short', 's')}</div>
                                                </div>
                                                <div className="col-6 text-end">
                                                    <div className="label-xs">{t('pnl', 'PNL')}</div>
                                                    <div className={`pnl-val ${pnlClass}`}>
                                                        {item.pnl ? (item.pnl >= 0 ? '+' : '-') : ''}
                                                        {currencySymbol}{item.pnl ? Math.abs(item.pnl).toFixed(2) : '0.00'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Footer Timestamp */}
                                        <div className="timestamp-footer mt-2">
                                            {formatDate(item.createdAt)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Filter Sheet */}
            {isFilterOpen && (
                <>
                    <div className="filter-overlay" onClick={() => setIsFilterOpen(false)}></div>
                    <div className="filter-sheet majestic-glass" style={{ background: 'linear-gradient(180deg, rgba(22, 27, 38, 0.98) 0%, rgba(11, 14, 17, 1) 100%)' }}>
                        <div className="filter-handle"></div>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h4 className="text-white fw-bold m-0">{t('filter_trades', 'Filter Trades')}</h4>
                            <i className="bi bi-x-lg text-secondary cursor-pointer" onClick={() => setIsFilterOpen(false)}></i>
                        </div>

                        <div className="filter-group">
                            <span className="filter-group-label">{t('asset_symbol', 'Asset Symbol')}</span>
                            <div className="filter-options-grid">
                                {uniqueAssets.map(asset => (
                                    <div
                                        key={asset}
                                        className={`filter-option ${filterAsset === asset ? 'active' : ''}`}
                                        onClick={() => setFilterAsset(asset)}
                                    >
                                        {asset}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="filter-group">
                            <span className="filter-group-label">{t('order_type', 'Order Type')}</span>
                            <div className="filter-options-grid">
                                {['ALL', 'BUY', 'SELL'].map(type => (
                                    <div
                                        key={type}
                                        className={`filter-option ${filterType === type ? 'active' : ''}`}
                                        onClick={() => setFilterType(type)}
                                    >
                                        {type === 'ALL' ? t('all', 'ALL') : type === 'BUY' ? t('buy', 'BUY') : t('sell', 'SELL')}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="filter-group">
                            <span className="filter-group-label">{t('duration', 'Duration')}</span>
                            <div className="filter-options-grid">
                                {['ALL', '30', '60', '120', '300', '600', '1800'].map(d => (
                                    <div
                                        key={d}
                                        className={`filter-option ${filterDuration === d ? 'active' : ''}`}
                                        onClick={() => setFilterDuration(d)}
                                    >
                                        {d === 'ALL' ? t('all', 'ALL') : parseInt(d) < 60 ? `${d}s` : `${parseInt(d) / 60}m`}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="filter-group mb-5">
                            <span className="filter-group-label">{t('leverage', 'Leverage')}</span>
                            <div className="filter-options-grid">
                                {['ALL', '1', '5', '10', '20', '50', '100'].map(l => (
                                    <div
                                        key={l}
                                        className={`filter-option ${filterLeverage === l ? 'active' : ''}`}
                                        onClick={() => setFilterLeverage(l)}
                                    >
                                        {l === 'ALL' ? t('all', 'ALL') : `x${l}`}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="filter-actions d-grid gap-3" style={{ gridTemplateColumns: '1fr 1.5fr' }}>
                            <button className="btn majestic-btn-outline" onClick={resetFilters}>{t('reset', 'Reset')}</button>
                            <button className="btn majestic-btn-primary" onClick={() => setIsFilterOpen(false)}>{t('apply_filters', 'Apply')}</button>
                        </div>
                    </div>
                </>
            )}

            {/* Bottom Navigation */}
            <div className="bottom-nav">
                <Link to="/dashboard" className="nav-item">
                    <i className="bi bi-house-door"></i>
                    <span>{t('home', 'Home')}</span>
                </Link>
                <Link to="/markets" className="nav-item">
                    <i className="bi bi-graph-up"></i>
                    <span>{t('market', 'Market')}</span>
                </Link>
                <Link to="/holdings" className="nav-item active">
                    <i className="bi bi-pie-chart-fill"></i>
                    <span>{t('holdings', 'Holdings')}</span>
                </Link>
                <Link to="/profile" className="nav-item">
                    <i className="bi bi-person"></i>
                    <span>{t('my_account', 'My Account')}</span>
                </Link>
            </div>
        </div>
    );
};

export default HoldingsPage;
